import React, { useEffect, useMemo, useState } from "react";
import {
    Checkbox, FormControlLabel, Tooltip, Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import { useMutation, useQuery } from "@tanstack/react-query";
import FormHelperText from "@mui/material/FormHelperText";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlineIcon from "@mui/icons-material/InfoOutlined";
import { useTranslate } from "../../../hooks/useTranslate";
import subjectSelectionService from "../../../services/subjectSelectionService";
import TextInput from "../../../components/TextInput";
import { formatTo } from "../../../utils/dateTimeHelper";
import { useGetYearGroups } from "../../../hooks/useGetYearGroups";
import { useGetStartDates } from "../../../hooks/useGetStartDates";
import Preloader from "../../../components/Preloader";
import { useGetCountries } from "../../../hooks/useGetCountries";
import { useGetGenders } from "../../../hooks/useGetGenders";
import axiosManagerRegister from "../../../services/axiosManagerRegister";
import { formatCurrency, formatPrice } from "../../../utils/formatCurrency";
import { isVersionUK } from "../../../services/versionService";
import { useToast } from "../../../context/toast";
import IconStripeLogo from "../../../assets/images/icons/stripe-logo.svg?react";
import { STEPS } from "../hooks/useAddStudent";

const currency = isVersionUK ? "GBP" : "USD";

export const Confirmation = (props) => {
    const {
        registrationId, childId, studySchedule, divisionSelected,
        studentsLength,
        detailsData,
        yearGroupSelected,
        packageSelected, onPackageChange,
        subjectsSelected,
        onPrev,
        setStep,
        onNext,
    } = props;
    const { t } = useTranslate();
    const toast = useToast();

    const [selectedContract, setSelectedContract] = useState(null);
    const [discountCode, setDiscountCode] = useState("");
    const [termsChecked, setTermsChecked] = useState(false);
    const [showTermsError, setShowTermsError] = useState(false);

    const { countriesOptions, isLoading: isLoadingCountries } = useGetCountries();
    const { yearGroupsOptions, isLoading: isLoadingYearGroups } = useGetYearGroups();
    const { startDatesOptions, isLoading: isLoadingStartDates } = useGetStartDates();
    const { gendersOptions, isLoading: isLoadingGenders } = useGetGenders();

    const {
        isFetching: isLoadingRegFee, data: regFee,
    } = useQuery({
        queryKey: [`add-student/${registrationId}/pricing/regfee`],
        queryFn: () => axiosManagerRegister.get$(`api/api/registration/${registrationId}/pricing/regfee`).then((response) => response?.data?.totalRegFeeDue),
        retry: 0,
    });
    const {
        isFetching: isLoadingContractsOptions, data: contractsOptionsAll, refetch: refetchContractsOptions,
    } = useQuery({
        queryKey: [`add-student/${registrationId}/${packageSelected?.productId}/contracts/options`],
        queryFn: () => axiosManagerRegister.get$(`api/api/registration/${registrationId}/contracts/options?numberOfExistingStudents=${studentsLength}`).then((response) => response?.data),
        retry: 0,
    });

    const contractsOptions = useMemo(() => (contractsOptionsAll?.children || [])?.[0]?.contractOptions, [contractsOptionsAll]);
    const discounts = useMemo(
        () => (contractsOptions || [])
            .map((item) => item?.contractPrice?.discountsApplied)
            .flat()
            .filter((d, index, arr) => arr.findIndex((x) => x?.code === d?.code) === index),
        [contractsOptions],
    );

    const {
        isFetching: isLoadingProduct, data: product,
    } = useQuery({
        queryKey: [`add-student/${yearGroupSelected}/${divisionSelected}/packages`, studySchedule, detailsData?.desiredStartDate],
        queryFn: () => axiosManagerRegister.post$(`/api/api/registration/${registrationId}/product`, {
            studySchedule, divisionId: divisionSelected, yearGroupId: yearGroupSelected?.yearGroupId, startDate: detailsData?.desiredStartDate,
        }).then((response) => response?.data?.packages),
        retry: 0,
        enabled: !!packageSelected?.productId && !packageSelected?.name && !!yearGroupSelected && !!divisionSelected && !!registrationId && !!studySchedule,
    });
    useEffect(() => {
        if (packageSelected?.productId && !packageSelected.name && !!product?.length) {
            const fullPackage = product.find((item) => item.productId === packageSelected.productId);
            onPackageChange(fullPackage);
        }
    }, [product, packageSelected]);

    const {
        mutateAsync: applyCode, isPending: isApplyingCode,
    } = useMutation({
        mutationFn: (requestData) => axiosManagerRegister.post$(`/api/api/registration/${registrationId}/discounts`, requestData).then((response) => response?.data),
        onError: () => {
            toast.error(t("common.errorMessage"));
        },
        onSuccess: (data) => {
            if (!data.isValid) toast.error(data.message);
        },
    });
    const {
        mutateAsync: deleteCode, isPending: isDeletingCode,
    } = useMutation({
        mutationFn: () => axiosManagerRegister.delete$(`/api/api/registration/${registrationId}/discounts`).then((response) => response?.data),
        onError: () => {
            toast.error(t("common.errorMessage"));
        },
    });

    const {
        mutateAsync: startPayment, isPending: isStartingPayment,
    } = useMutation({
        mutationFn: (requestData) => axiosManagerRegister.post$(`/api/api/paymentSession/${registrationId}/RegFee`, requestData).then((response) => response?.data),
        onError: () => {
            toast.error(t("common.errorMessage"));
        },
    });

    const countryOfResidence = useMemo(() => (detailsData?.countryOfResidence
        ? countriesOptions.find((item) => item.value === detailsData?.countryOfResidence)?.label
        : "-"), [detailsData?.countryOfResidence, countriesOptions?.length]);
    const desiredStartDate = useMemo(() => (detailsData?.desiredStartDate
        ? startDatesOptions.find((item) => item.value.includes(detailsData?.desiredStartDate))?.label
        : "-"), [detailsData?.desiredStartDate, startDatesOptions?.length]);
    const currentNotionalYearGroup = useMemo(() => (detailsData?.currentNotionalYearGroup
        ? yearGroupsOptions.find((item) => item.value === detailsData?.currentNotionalYearGroup)?.label
        : "-"), [detailsData?.currentNotionalYearGroup, yearGroupsOptions?.length]);
    const genderType = useMemo(() => (detailsData?.genderTypeId
        ? gendersOptions.find((item) => item.value === detailsData?.genderTypeId)?.label
        : "-"), [detailsData?.genderTypeId, gendersOptions?.length]);

    const subjectNames = useMemo(() => subjectSelectionService
        .getSelectedSubjects(packageSelected, subjectsSelected)
        .map((s) => s.name)
        .join("; "), [packageSelected, subjectsSelected]);

    const generateInstalmentQuote = (installmentsFull, isRolling) => {
        if (!installmentsFull || !installmentsFull.length) return;

        const installments = installmentsFull?.slice()?.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        const firstInstallment = installments[0].amount;
        const allSame = installments.every(
            (inst) => inst.amount.toFixed(0) === firstInstallment.toFixed(0),
        );

        if (isVersionUK && isRolling) return null;

        if (allSame) {
            return t("addStudent.contracts.Rolling.monthlyInstalment", {
                price: formatPrice(firstInstallment, isVersionUK),
                count: installments.length || 0,
            });
        }
        const secondInstallment = installments[1]?.amount;
        return t("addStudent.contracts.Rolling.monthlyInstalment2", {
            price: formatPrice(firstInstallment, isVersionUK),
            price2: formatPrice(secondInstallment, isVersionUK),
            count: installments.length || 0,
        });
    };

    const onApplyCode = async () => {
        try {
            await applyCode({ discountCode });
            refetchContractsOptions();
        } catch {
            // Error is in Mutation
        }
    };
    const onRemoveCode = async () => {
        try {
            await deleteCode();
            refetchContractsOptions();
        } catch {
            // Error is in Mutation
        }
    };
    const onPayClick = async () => {
        if (!termsChecked) {
            setShowTermsError(true);
            return;
        }

        try {
            const response = await startPayment({
                returnURL: `${window.location.origin}/add-student/new?complete`,
                cancelURL: window.location.href,
            });
            if (response.url) {
                window.location.href = response.url;
            }
        } catch {
            // Error in Mutatiom
        }
    };

    return (
        <Preloader
            isLoading={isLoadingProduct || isLoadingRegFee || isLoadingCountries || isLoadingYearGroups || isLoadingStartDates || isLoadingGenders}
            overflow
            showInitiallyOverflow
        >
            <div className="details-form">
                <Typography variant="h1">
                    {t("addStudent.confirmation")}
                </Typography>
                <Typography variant="body1" component="div">
                    {t("addStudent.confirmationSub")}
                </Typography>
                <Typography variant="h3" className="add-student__title-boxed mt-8">
                    {t("addStudent.selectContractLength")}
                </Typography>
                <Typography variant="body1" component="div" className="mt-4">
                    {t("addStudent.contractText1")}
                    <b>{t("addStudent.contractTextSave")}</b>
                    <br />
                    {t("addStudent.contractText2")}
                </Typography>
                <Typography variant="h5" className="mt-8">
                    {t("addStudent.feesTitle")}
                </Typography>
                <Typography variant="body1" component="div">
                    {t("addStudent.feesText")}
                </Typography>
                <Preloader isLoading={isLoadingContractsOptions}>
                    <div className="flex flex-wrap align-stretch gap-8 mt-4">
                        {(contractsOptions || []).map((contract) => (
                            <div
                                key={contract.productId}
                                className="add-student-selection-option add-student-selection-option--contract"
                            >
                                <span className="add-student-selection-option__primary-text">
                                    {t(`addStudent.contracts.${contract.type}.title`)}
                                </span>
                                <span className="add-student-selection-option__bold-text">
                                    {contract.type === "Rolling"
                                        ? t(`addStudent.contracts.${contract.type}.oneOffPayment`, { price: formatPrice(contract.contractPrice?.totalBeforeDiscount || 0, isVersionUK) })
                                        : t(`addStudent.contracts.${contract.type}.oneOffPayment`, { price: formatPrice(contract.contractPrice?.totalBeforeDiscount || 0, isVersionUK) })}
                                </span>
                                {generateInstalmentQuote(contract.contractMonthlyInstalments, contract.type === "Rolling") ? (
                                    <>
                                        <div className="divider mt-2 mb-2">
                                            <span>{t("common.or")}</span>
                                        </div>
                                        <span className="add-student-selection-option__bold-text">
                                            {generateInstalmentQuote(contract.contractMonthlyInstalments, contract.type === "Rolling")}
                                        </span>
                                    </>
                                ) : null}
                                <span>
                                    {t(`addStudent.contracts.${contract.type}.description`)}
                                </span>
                                {/* {contract.contractPrice?.discountsApplied?.length > 0 && (
                                    <span className="add-student-selection-option--contract__discount">
                                        {t("addStudent.discountApplied")}
                                        <Tooltip
                                            arrow
                                            className="tooltip add-student-selection-option--contract__discount-tooltip"
                                            title={`-${formatCurrency(currency).format(contract.contractPrice?.discountsApplied?.[0]?.value)}`}
                                        >
                                            <IconButton><InfoOutlineIcon /></IconButton>
                                        </Tooltip>
                                    </span>
                                )} */}
                            </div>
                        ))}
                    </div>
                </Preloader>
                <div className="flex align-center justify-between gap-6 mt-12 mb-4">
                    <Typography variant="h4" component="h4" className="text-primary">
                        {`${detailsData.forename} ${detailsData.surname}`}
                    </Typography>
                    <Button
                        className="btn-link"
                        disabled={isStartingPayment}
                        onClick={() => setStep(STEPS.DETAILS, true)}
                    >
                        {t("addStudent.edit")}
                    </Button>
                </div>
                <div className="add-student__confirmation-details">
                    <Typography variant="body1" component="div">
                        <b>{t("addStudent.dateOfBirth")}</b>
                        {detailsData?.dateOfBirth ? formatTo(detailsData?.dateOfBirth, "dd/MM/yyyy", "yyyy-MM-dd") : "-"}
                    </Typography>
                    <Typography variant="body1" component="div">
                        <b>{t("addStudent.country")}</b>
                        {countryOfResidence}
                    </Typography>
                    <Typography variant="body1" component="div">
                        <b>{t("addStudent.desiredStartDate")}</b>
                        {desiredStartDate}
                    </Typography>
                    <Typography variant="body1" component="div">
                        <b>{t("addStudent.currentYearGroup")}</b>
                        {currentNotionalYearGroup}
                    </Typography>
                    <Typography variant="body1" component="div">
                        <b>{t("addStudent.gender")}</b>
                        {genderType}
                    </Typography>
                </div>
                <div className="divider mt-8 mb-8" />
                <div className="flex align-center justify-between gap-6 mt-12 mb-4">
                    <Typography variant="h4" component="h4" className="text-primary">
                        {t("addStudent.package")}
                    </Typography>
                </div>
                <div className="flex align-center mb-2 gap-4">
                    <Typography variant="body1" component="div">
                        <b>{t("addStudent.primary")}</b>
                        {packageSelected?.name || "-"}
                    </Typography>
                    <Button
                        className="btn-link"
                        disabled={isStartingPayment}
                        onClick={() => setStep(STEPS.YEAR_GROUP, true)}
                    >
                        {t("addStudent.edit")}
                    </Button>
                </div>
                <div className="flex align-center mb-2 gap-4">
                    <Typography variant="body1" component="div">
                        <b>{t("addStudent.selectedSubjects")}</b>
                        {subjectNames || "-"}
                    </Typography>
                    <Button
                        className="btn-link"
                        disabled={isStartingPayment}
                        onClick={() => setStep(STEPS.SUBJECTS, true)}
                    >
                        {t("addStudent.edit")}
                    </Button>
                </div>
                {/* <div className="flex align-center mb-2 gap-4"> */}
                {/*    <Typography variant="body1" component="div"> */}
                {/*        <b>{t("addStudent.contractLength")}</b> */}
                {/*        {selectedContract || "-"} */}
                {/*    </Typography> */}
                {/* </div> */}
                <div className="divider mt-8 mb-8" />
                <div className="add-student__apply-code">
                    <div className="flex align-end gap-4">
                        <TextInput
                            label={t("addStudent.discountCode")}
                            placeholder={t("addStudent.discountCodePlaceholder")}
                            name="discountCode"
                            value={discountCode}
                            disabled={isApplyingCode || isDeletingCode}
                            onChange={(e) => setDiscountCode(e.target.value)}
                        />
                        <Button
                            className="button-primary"
                            loading={isApplyingCode}
                            onClick={onApplyCode}
                        >
                            {t("addStudent.applyCode")}
                        </Button>
                    </div>
                    {discounts?.length > 0 && (
                        <div className="discounts-block__applied mt-4">
                            <Typography variant="body1" component="div">{t("addStudent.discountCodeTitle")}</Typography>
                            <Typography variant="body1" component="div" className="discounts-block__applied__description">
                                {t("addStudent.discountCodeDescription")}
                            </Typography>
                            {discounts.map((discount) => (
                                <div key={discount.id} className="discounts-block__applied__code">
                                    {`${discount.code}`}
                                    <IconButton
                                        aria-label="close"
                                        onClick={() => onRemoveCode(discount.code)}
                                        disableRipple
                                        disabled={isDeletingCode}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="divider mt-8 mb-8" />
                <FormControlLabel
                    className="add-student__agree-terms"
                    control={<Checkbox checked={termsChecked} onChange={(e) => setTermsChecked(!termsChecked)} />}
                    label={(
                        <>
                            {t("addStudent.agreeCheckbox")}
                            <a href="https://kingsinterhigh.co.uk/terms-of-use/" target="_blank" rel="noreferrer">{t("addStudent.termsAndConditions")}</a>
                        </>
                    )}
                />
                {showTermsError && !termsChecked ? <FormHelperText className="text-error">{t("formErrors.required")}</FormHelperText> : null}
                <div className="add-student__pay-block mt-8">
                    <div>
                        <Typography variant="h4" component="div" className="text-primary">
                            {t("addStudent.registrationFee")}
                        </Typography>
                        <Typography variant="h5" component="div" className="text-primary">
                            {t("addStudent.nonRefundable")}
                        </Typography>
                    </div>
                    <Typography variant="h4" component="div" className="text-primary text-center">
                        {t("addStudent.totalToPay")}
                        <br />
                        {formatCurrency(currency).format(regFee || 0)}
                    </Typography>
                    <div>
                        <IconStripeLogo />
                    </div>
                    <Button
                        type="submit"
                        loading={isStartingPayment}
                        onClick={onPayClick}
                        className="button-accent"
                    >
                        {t("addStudent.payNow")}
                    </Button>
                </div>
            </div>
        </Preloader>
    );
};
